import { t, useI18n } from '../../i18n';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layer, Source } from '@vis.gl/react-maplibre';
import type { MapRef, MarkerDragEvent } from '@vis.gl/react-maplibre';
import MapLibreMap from '../../components/maps/MapLibreMap';
import MapDotMarker from '../../components/maps/MapDotMarker';
import MapPinMarker from '../../components/maps/MapPinMarker';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import { postService } from '../../services/postService';
import { checkpointService } from '../../services/checkpointService';
import { sanitizeText, sanitizeMultilineText, sanitizeNumber } from '../../utils/sanitize';
import {
  createCircleFeature,
  createFeatureCollection,
  createPolygonFeature,
  getBoundsFromPositions,
  parseGeometryPoint,
  parseGeometryPolygon,
  type LatLngTuple,
} from '../../utils/geo';
import logoSrc from '../../assets/Logo_Master.svg';
import './CreatePatrolPage.css';

const DEFAULT_VIEW_STATE = {
  latitude: 53.3498,
  longitude: -6.2603,
  zoom: 13,
};

interface Site {
  id: string;
  name: string;
  client_contract_id?: string | null;
  client_contract?: { name?: string | null } | null;
  attendance_radius_m?: number | null;
  geofence_type?: 'radius' | 'polygon' | string | null;
  geofence_radius_m?: number | null;
  geofence_polygon?: unknown;
  location?: unknown;
  [key: string]: unknown;
}

interface Post {
  id: string;
  name: string;
  description?: string | null;
  geofence_radius_m?: number | null;
  location?: unknown;
  [key: string]: unknown;
}

interface MappedPost extends Post {
  position: [number, number];
}

interface Props {
  clientContractId?: string | null;
  clientName?: string | null;
  onExit?: () => void;
  onReplayTour?: () => void;
}

function formatRadius(radiusMetres: number | null | undefined): string {
  const radius = radiusMetres ?? 50;
  if (radius < 1000) return `${Math.round(radius)} m radius`;
  return `${(radius / 1000).toFixed(1)} km radius`;
}

function formatCoordinates(position: [number, number] | null): string {
  if (!position) return t('No map point');
  return `${position[0].toFixed(5)}, ${position[1].toFixed(5)}`;
}

function getPolygonPositionsFromFeature(
  feature: ReturnType<typeof createPolygonFeature> | ReturnType<typeof createCircleFeature>,
): LatLngTuple[] {
  const coordinates = feature?.geometry.coordinates[0] ?? [];
  return coordinates.map(([lng, lat]) => [lat, lng] as LatLngTuple);
}

export default function CreatePostsPage({
  clientContractId = null,
  clientName = null,
  onExit,
  onReplayTour,
}: Props) {
  useI18n();
  const mapRef = useRef<MapRef | null>(null);

  const [sites, setSites] = useState<Site[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [existingPosts, setExistingPosts] = useState<Post[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [geofenceRadius, setGeofenceRadius] = useState('50');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [draftInspectorOpen, setDraftInspectorOpen] = useState(false);
  const [existingOpen, setExistingOpen] = useState(true);
  const [clearConfirmPending, setClearConfirmPending] = useState(false);
  const [exitConfirmPending, setExitConfirmPending] = useState(false);
  const [deleteConfirmPostId, setDeleteConfirmPostId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDraggingDraft, setIsDraggingDraft] = useState(false);
  const [toast, setToast] = useState<{ msg: string; isError?: boolean } | null>(null);

  const showToast = useCallback((msg: string, isError = false) => {
    setToast({ msg, isError });
    window.setTimeout(() => setToast(null), 2400);
  }, []);

  const loadSites = useCallback(async () => {
    try {
      const data = await checkpointService.fetchSites();
      const rows = data as Site[];
      setSites(clientContractId
        ? rows.filter((site) => site.client_contract_id === clientContractId)
        : rows);
    } catch (err) {
      console.error('Error loading sites:', (err as Error).message);
      setError(t('Unable to load sites for post setup'));
    }
  }, [clientContractId]);

  const loadPosts = useCallback(async (siteId: string) => {
    if (!siteId) {
      setExistingPosts([]);
      return;
    }

    try {
      const data = await postService.fetchPostsBySite(siteId);
      setExistingPosts(data as Post[]);
    } catch (err) {
      console.error('Error loading posts:', (err as Error).message);
      setExistingPosts([]);
      setError(t('Unable to load posts for this site'));
    }
  }, []);

  const clearDraft = useCallback(() => {
    setLatitude('');
    setLongitude('');
    setSelectedPostId(null);
    setDraftInspectorOpen(false);
    setClearConfirmPending(false);
  }, []);

  const resetForm = useCallback(() => {
    setName('');
    setDescription('');
    setGeofenceRadius('50');
    clearDraft();
  }, [clearDraft]);

  const handleSiteChange = useCallback((siteId: string) => {
    setSelectedSiteId(siteId);
    setSelectedPostId(null);
    setDraftInspectorOpen(false);
    setLatitude('');
    setLongitude('');
    setError('');
    setSuccess('');
  }, []);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      await loadSites();
      setIsLoading(false);
    };
    init();
  }, [loadSites]);

  useEffect(() => {
    if (selectedSiteId) {
      loadPosts(selectedSiteId);
    } else {
      setExistingPosts([]);
    }
  }, [loadPosts, selectedSiteId]);

  useEffect(() => {
    if (!selectedSiteId) return;
    if (!sites.some((site) => site.id === selectedSiteId)) {
      handleSiteChange('');
      setExistingPosts([]);
    }
  }, [handleSiteChange, selectedSiteId, sites]);

  const selectedSite = useMemo(
    () => sites.find((site) => site.id === selectedSiteId) ?? null,
    [selectedSiteId, sites],
  );

  const siteFallbackCenter = useMemo(
    () => parseGeometryPoint(selectedSite?.location) as [number, number] | null,
    [selectedSite],
  );

  const sitePolygonPositions = useMemo(
    () => parseGeometryPolygon(selectedSite?.geofence_polygon) ?? [],
    [selectedSite],
  );

  const siteGeofenceRadius = useMemo(
    () => selectedSite?.geofence_radius_m ?? selectedSite?.attendance_radius_m ?? 300,
    [selectedSite],
  );

  const siteGeofenceFeature = useMemo(() => {
    if (!selectedSite) return null;

    if (selectedSite.geofence_type === 'polygon' && sitePolygonPositions.length >= 3) {
      return createPolygonFeature(sitePolygonPositions, { kind: 'site-geofence' });
    }

    return siteFallbackCenter
      ? createCircleFeature(siteFallbackCenter, siteGeofenceRadius, 96, { kind: 'site-geofence' })
      : null;
  }, [selectedSite, siteFallbackCenter, siteGeofenceRadius, sitePolygonPositions]);

  const siteGeofencePositions = useMemo(
    () => getPolygonPositionsFromFeature(siteGeofenceFeature),
    [siteGeofenceFeature],
  );

  const siteGeofenceLabel = useMemo(() => {
    if (!selectedSite) return t('Select a site');
    if (selectedSite.geofence_type === 'polygon' && sitePolygonPositions.length >= 3) {
      return `Site geofence bounds (${sitePolygonPositions.length} points)`;
    }
    return formatRadius(siteGeofenceRadius);
  }, [selectedSite, siteGeofenceRadius, sitePolygonPositions.length]);

  const radiusNumber = useMemo(
    () => sanitizeNumber(geofenceRadius, {
      min: 5,
      max: 10000,
      integer: true,
      defaultValue: 50,
    }) ?? 50,
    [geofenceRadius],
  );

  const pendingPosition = useMemo(() => {
    if (!latitude || !longitude) return null;
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return [lat, lng] as [number, number];
  }, [latitude, longitude]);

  const mappedPosts = useMemo((): MappedPost[] => existingPosts
    .map((post) => ({
      ...post,
      position: parseGeometryPoint(post.location) as [number, number] | null,
    }))
    .filter((post): post is MappedPost => post.position !== null), [existingPosts]);

  const existingPostGeofences = useMemo(() => createFeatureCollection(
    mappedPosts.map((post) => (
      post.geofence_radius_m
        ? createCircleFeature(post.position, post.geofence_radius_m, 64, { kind: 'existing-post' })
        : null
    )),
  ), [mappedPosts]);

  const pendingGeofence = useMemo(
    () => (pendingPosition
      ? createCircleFeature(pendingPosition, radiusNumber, 64, { kind: 'draft-post' })
      : null),
    [pendingPosition, radiusNumber],
  );

  const selectedExistingPost = useMemo(
    () => existingPosts.find((post) => post.id === selectedPostId) ?? null,
    [existingPosts, selectedPostId],
  );

  const selectedExistingPosition = useMemo(
    () => parseGeometryPoint(selectedExistingPost?.location) as [number, number] | null,
    [selectedExistingPost],
  );

  const selectedExistingMapIndex = useMemo(
    () => mappedPosts.findIndex((post) => post.id === selectedPostId),
    [mappedPosts, selectedPostId],
  );

  const inspectorMode = selectedExistingPost
    ? 'existing'
    : pendingPosition && draftInspectorOpen
      ? 'draft'
      : null;

  const fitPoints = useMemo(() => {
    if (selectedExistingPosition) return [selectedExistingPosition];
    if (pendingPosition) return [pendingPosition];
    return [
      ...siteGeofencePositions,
      ...mappedPosts.map((post) => post.position),
    ];
  }, [mappedPosts, pendingPosition, selectedExistingPosition, siteGeofencePositions]);

  const fitPointsKey = useMemo(
    () => fitPoints.map((point) => `${point[0]},${point[1]}`).join('|'),
    [fitPoints],
  );

  useEffect(() => {
    if (isDraggingDraft || !mapRef.current) return;

    if (fitPoints.length > 1) {
      const bounds = getBoundsFromPositions(fitPoints);
      if (bounds) {
        mapRef.current.fitBounds(bounds, { padding: 80, maxZoom: 17, duration: 900 });
      }
      return;
    }

    if (fitPoints.length === 1) {
      mapRef.current.flyTo({
        center: [fitPoints[0][1], fitPoints[0][0]],
        zoom: 16.4,
        duration: 900,
      });
      return;
    }

    if (siteFallbackCenter) {
      mapRef.current.flyTo({
        center: [siteFallbackCenter[1], siteFallbackCenter[0]],
        zoom: 15.2,
        duration: 900,
      });
    }
  }, [fitPoints, fitPointsKey, isDraggingDraft, siteFallbackCenter]);

  const handleMapClick = useCallback((event: { lngLat: { lat: number; lng: number } }) => {
    if (!selectedSiteId) return;
    setLatitude(event.lngLat.lat.toFixed(6));
    setLongitude(event.lngLat.lng.toFixed(6));
    setSelectedPostId(null);
    setDraftInspectorOpen(true);
    setError('');
    setSuccess('');
  }, [selectedSiteId]);

  const handleDraftDragStart = useCallback(() => {
    setIsDraggingDraft(true);
    setSelectedPostId(null);
    setDraftInspectorOpen(true);
  }, []);

  const handleDraftDrag = useCallback((event: MarkerDragEvent) => {
    setLatitude(event.lngLat.lat.toFixed(6));
    setLongitude(event.lngLat.lng.toFixed(6));
  }, []);

  const handleDraftDragEnd = useCallback((event: MarkerDragEvent) => {
    setLatitude(event.lngLat.lat.toFixed(6));
    setLongitude(event.lngLat.lng.toFixed(6));
    setIsDraggingDraft(false);
  }, []);

  const focusExistingPost = useCallback((post: Post) => {
    setSelectedPostId(post.id);
    setDraftInspectorOpen(false);
    setClearConfirmPending(false);
  }, []);

  const handleSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    const cleanName = sanitizeText(name, 100);
    const cleanDescription = sanitizeMultilineText(description, 1000);
    const cleanLat = latitude ? sanitizeNumber(latitude, { min: -90, max: 90 }) : null;
    const cleanLng = longitude ? sanitizeNumber(longitude, { min: -180, max: 180 }) : null;
    const cleanRadius = sanitizeNumber(geofenceRadius, {
      min: 5,
      max: 10000,
      integer: true,
      defaultValue: 50,
    });

    if (!cleanName || !selectedSiteId || cleanLat == null || cleanLng == null) {
      setError(t('Post name, site, and map location are required'));
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await postService.createPost({
        name: cleanName,
        description: cleanDescription || null,
        siteId: selectedSiteId,
        latitude: cleanLat,
        longitude: cleanLng,
        geofenceRadiusM: cleanRadius ?? 50,
      });

      await loadPosts(selectedSiteId);
      resetForm();
      setSuccess(t('Post saved'));
      showToast('Post saved');
    } catch (err) {
      const message = (err as Error).message || 'Failed to create post';
      setError(message);
      showToast(message, true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteConfirmPostId) return;
    setIsDeleting(true);
    try {
      await postService.deletePost(deleteConfirmPostId);
      setDeleteConfirmPostId(null);
      setSelectedPostId((current) => (current === deleteConfirmPostId ? null : current));
      if (selectedSiteId) await loadPosts(selectedSiteId);
      showToast('Post deleted');
    } catch (err) {
      const message = `Failed to delete post: ${(err as Error).message}`;
      setError(message);
      showToast(message, true);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteConfirmPostId, loadPosts, selectedSiteId, showToast]);

  const handleExitRequest = useCallback(() => {
    if (!onExit) return;
    if (name.trim() || description.trim() || pendingPosition) {
      setExitConfirmPending(true);
      return;
    }
    onExit();
  }, [description, name, onExit, pendingPosition]);

  const canSave = Boolean(selectedSiteId && name.trim() && pendingPosition && !isSubmitting);
  const rangeRadius = Math.min(Math.max(radiusNumber, 5), 500);
  const radiusProgress = ((rangeRadius - 5) / 495) * 100;

  if (isLoading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div className="pb-wrapper">
      <div className="pb-app pb-app--posts">
        <header className="pb-top">
          {onExit ? (
            <button
              type="button"
              className="pb-top__brand pb-top__brand--btn"
              onClick={handleExitRequest}
              title={t('Back to sites')}
            >
              <img src={logoSrc} alt={t('OdinsEye')} className="pb-top__logo-img" />
              <div className="pb-top__title-stack">
                <span className="pb-top__title">OdinsEye</span>
                <span className="pb-top__sub">{t('Post builder')}</span>
              </div>
            </button>
          ) : (
            <div className="pb-top__brand">
              <img src={logoSrc} alt={t('OdinsEye')} className="pb-top__logo-img" />
              <div className="pb-top__title-stack">
                <span className="pb-top__title">OdinsEye</span>
                <span className="pb-top__sub">{t('Post builder')}</span>
              </div>
            </div>
          )}

          <div className="pb-top__crumbs">
            <span className="pb-top__crumb">{t('Sites & Patrols')}</span>
            <span className="pb-top__crumb-sep">/</span>
            <span className="pb-top__crumb">{selectedSite?.name ?? t('Select a site')}</span>
            <span className="pb-top__crumb-sep">/</span>
            <span className="pb-top__crumb pb-top__crumb--active">
              {selectedExistingPost ? selectedExistingPost.name : 'New post'}
            </span>
            {(success || isSubmitting) && (
              <span className="pb-top__autosave">
                <span className="pb-top__autosave-dot" />
                {isSubmitting ? 'Saving...' : success}
              </span>
            )}
          </div>

          <div className="pb-top__right" data-tour="post-builder-actions">
            {onExit && (
              <button type="button" className="pb-btn pb-btn--ghost pb-btn--exit" onClick={handleExitRequest}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
                {t('Back to Sites')}
              </button>
            )}
            {onReplayTour && (
              <button type="button" className="pb-btn pb-btn--ghost" onClick={onReplayTour}>
                {t('Help')}
              </button>
            )}
            <button type="button" className="pb-btn pb-btn--ghost" onClick={clearDraft} disabled={!pendingPosition}>
              {t('Clear point')}
            </button>
            <button type="button" className="pb-btn pb-btn--primary" onClick={() => handleSubmit()} disabled={!canSave}>
              {isSubmitting ? 'Saving...' : 'Save post'}
            </button>
          </div>
        </header>

        <div className={`pb-body${!inspectorMode ? ' pb-body--no-inspector' : ''}`}>
          <aside className="pb-rail">
            <div className="pb-rail__scroll">
              <div className="pb-card" data-tour="post-builder-site">
                <div className="pb-card__title">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a7 7 0 017 7c0 5-7 13-7 13S5 14 5 9a7 7 0 017-7z" /><circle cx="12" cy="9" r="2.5" /></svg>
                  {t('Scope')}
                </div>
                {clientName && (
                  <div className="pb-site__meta">
                    <span className="pb-site__meta-item">{t('Client')} <strong>{clientName}</strong></span>
                  </div>
                )}
                <select
                  value={selectedSiteId}
                  onChange={(event) => handleSiteChange(event.target.value)}
                  className="form__select form__select--sm pb-select"
                  aria-label={t('Site')}
                >
                  <option value="">{sites.length === 0 ? 'No sites available' : 'Select a site...'}</option>
                  {sites.map((site) => (
                    <option key={site.id} value={site.id}>{site.name}</option>
                  ))}
                </select>
                {selectedSite && (
                  <div className="pb-site__meta">
                    <span className="pb-site__meta-item">
                      <strong>{siteGeofenceLabel}</strong>
                    </span>
                    <span className="pb-site__meta-sep" />
                    <span className="pb-site__meta-item">{existingPosts.length} posts</span>
                  </div>
                )}
              </div>

              <div className="pb-card" data-tour="post-builder-details">
                <div className="pb-card__title">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 113 3L7 19l-4 1 1-4Z" /></svg>
                  {t('New post')}
                </div>

                <form onSubmit={handleSubmit} className="pb-post-form">
                  {error && <div className="pb-error-banner">{error}</div>}
                  {success && <div className="pb-success-banner">{success}</div>}

                  <div className="pb-field">
                    <label className="pb-field__label">{t('Post name')}</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      className="pb-input"
                      placeholder={t('Main entrance')}
                      disabled={!selectedSiteId}
                    />
                  </div>

                  <div className="pb-field">
                    <label className="pb-field__label">{t('Description')}</label>
                    <textarea
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                      rows={3}
                      className="pb-textarea"
                      placeholder={t('Operational notes for guards')}
                      disabled={!selectedSiteId}
                    />
                  </div>

                  <div className="pb-field">
                    <label className="pb-field__label">Geofence radius - {radiusNumber} m</label>
                    <div className="pb-radius-slider">
                      <div className="pb-radius-slider__row">
                        <input
                          type="range"
                          min="5"
                          max="500"
                          step="5"
                          value={rangeRadius}
                          style={{ '--p': `${radiusProgress}%` } as React.CSSProperties}
                          onChange={(event) => setGeofenceRadius(event.target.value)}
                          disabled={!selectedSiteId}
                        />
                        <input
                          type="number"
                          min="5"
                          max="10000"
                          value={geofenceRadius}
                          onChange={(event) => setGeofenceRadius(event.target.value)}
                          className="pb-input pb-input--mono pb-post-radius-input"
                          disabled={!selectedSiteId}
                          aria-label={t('Geofence radius in metres')}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pb-post-location">
                    <span>{t('Location')}</span>
                    <strong>{formatCoordinates(pendingPosition)}</strong>
                    {pendingPosition && (
                      <button type="button" onClick={clearDraft}>{t('Clear')}</button>
                    )}
                  </div>

                  <button type="submit" disabled={!canSave} className="pb-btn pb-btn--primary pb-post-submit">
                    {isSubmitting ? 'Saving...' : 'Save post'}
                  </button>
                </form>
              </div>

              {selectedSiteId && (
                <div className={`pb-disclosure${existingOpen ? ' pb-disclosure--open' : ''}`} data-tour="post-builder-library">
                  <button type="button" className="pb-disclosure__head" onClick={() => setExistingOpen((open) => !open)}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6h11" /><path d="M9 12h11" /><path d="M9 18h11" /><path d="M5 6h.01" /><path d="M5 12h.01" /><path d="M5 18h.01" /></svg>
                    Existing posts
                    <small>{existingPosts.length}</small>
                    <span className="pb-disclosure__chev">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
                    </span>
                  </button>

                  {existingOpen && (
                    <div className="pb-disclosure__body">
                      {existingPosts.length === 0 ? (
                        <div className="pb-post-list-empty">{t('No posts for this site yet.')}</div>
                      ) : (
                        <div className="pb-existing">
                          {existingPosts.map((post) => {
                            const postPosition = parseGeometryPoint(post.location) as [number, number] | null;
                            return (
                              <div
                                key={post.id}
                                className={`pb-existing__item pb-existing__item--button${selectedPostId === post.id ? ' pb-existing__item--active' : ''}`}
                                onClick={() => focusExistingPost(post)}
                              >
                                <div className="pb-existing__main">
                                  <span className="pb-existing__name">{post.name}</span>
                                  <span className="pb-existing__meta">
                                    {post.geofence_radius_m ?? 50}m
                                    {postPosition ? ` - ${formatCoordinates(postPosition)}` : ' - no map point'}
                                  </span>
                                </div>
                                <div className="pb-existing__actions">
                                  <button
                                    type="button"
                                    className="pb-existing__view"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      focusExistingPost(post);
                                    }}
                                  >
                                    {t('View')}
                                  </button>
                                  <button
                                    type="button"
                                    className="pb-existing__del"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setDeleteConfirmPostId(post.id);
                                    }}
                                  >
                                    {t('Del')}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </aside>

          <main className="pb-canvas" data-tour="post-builder-map">
            <div className="pb-map">
              <MapLibreMap
                ref={mapRef}
                initialViewState={DEFAULT_VIEW_STATE}
                style={{ width: '100%', height: '100%' }}
                cursor={selectedSiteId ? (isDraggingDraft ? 'grabbing' : 'crosshair') : 'default'}
                onClick={handleMapClick}
              >
                {siteGeofenceFeature ? (
                  <Source id="post-selected-site-geofence-source" type="geojson" data={siteGeofenceFeature}>
                    <Layer id="post-selected-site-geofence-fill" type="fill" paint={{ 'fill-color': '#22c7a8', 'fill-opacity': 0.12 }} />
                    <Layer id="post-selected-site-geofence-line" type="line" paint={{ 'line-color': '#22c7a8', 'line-width': 2.5, 'line-opacity': 0.88 }} />
                  </Source>
                ) : null}

                {existingPostGeofences.features.length > 0 && (
                  <Source id="post-existing-geofences" type="geojson" data={existingPostGeofences}>
                    <Layer id="post-existing-geofences-fill" type="fill" paint={{ 'fill-color': '#ff8f4d', 'fill-opacity': 0.08 }} />
                    <Layer id="post-existing-geofences-line" type="line" paint={{ 'line-color': '#ff8f4d', 'line-width': 1.5, 'line-opacity': 0.5 }} />
                  </Source>
                )}

                {pendingGeofence && (
                  <Source id="post-pending-geofence" type="geojson" data={pendingGeofence}>
                    <Layer id="post-pending-geofence-fill" type="fill" paint={{ 'fill-color': '#1690bf', 'fill-opacity': 0.16 }} />
                    <Layer id="post-pending-geofence-line" type="line" paint={{ 'line-color': '#1690bf', 'line-width': 2.5, 'line-opacity': 0.85 }} />
                  </Source>
                )}

                {siteFallbackCenter && (
                  <MapPinMarker
                    position={siteFallbackCenter}
                    color="#22c7a8"
                    label="S"
                    size="sm"
                    title={selectedSite?.name || 'Site'}
                  />
                )}

                {mappedPosts.map((post, index) => (
                  <MapDotMarker
                    key={post.id}
                    position={post.position}
                    color={selectedPostId === post.id ? '#22c7a8' : '#ff8f4d'}
                    size={selectedPostId === post.id ? 22 : 18}
                    label={index + 1}
                    title={post.name}
                    onClick={() => focusExistingPost(post)}
                  />
                ))}

                {pendingPosition && (
                  <MapPinMarker
                    position={pendingPosition}
                    color="#1690bf"
                    label="+"
                    size="lg"
                    title={t('New post location')}
                    draggable
                    onClick={() => {
                      setSelectedPostId(null);
                      setDraftInspectorOpen(true);
                    }}
                    onDragStart={handleDraftDragStart}
                    onDrag={handleDraftDrag}
                    onDragEnd={handleDraftDragEnd}
                  />
                )}
              </MapLibreMap>
            </div>

            {!selectedSiteId && (
              <div className="pb-map__blocker">
                <div className="pb-map__blocker-icon">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a7 7 0 017 7c0 5-7 13-7 13S5 14 5 9a7 7 0 017-7z" /><circle cx="12" cy="9" r="2.5" /></svg>
                </div>
                <strong>{t('Pick a site to start')}</strong>
                <span>{t('The selected site\'s geofence and saved posts appear here. Click inside the map to place a fixed guard post.')}</span>
              </div>
            )}

            {selectedSiteId && (
              <div className="pb-overlay pb-overlay--top">
                <div className="pb-glass pb-stats">
                  <div className="pb-stats__cell">{t('Posts')} <strong>{existingPosts.length}</strong></div>
                  <div className="pb-stats__cell">{t('Mapped')} <strong>{mappedPosts.length}</strong></div>
                  <div className={`pb-stats__cell${pendingPosition ? ' pb-stats__cell--ok' : ''}`}>
                    Draft <strong>{pendingPosition ? 'placed' : 'unset'}</strong>
                  </div>
                  <div className="pb-stats__cell">{t('Radius')} <strong>{radiusNumber} m</strong></div>
                </div>
              </div>
            )}

            {selectedSiteId && (
              <div className="pb-overlay pb-overlay--tl">
                <span className="pb-mode-pill">
                  <span className="pb-mode-pill__dot" />
                  {t('Place mode - click map')}
                </span>
              </div>
            )}

            {selectedSiteId && (
              <div className="pb-overlay pb-overlay--tr">
                <div className="pb-zoom">
                  <button type="button" className="pb-zoom__btn" title={t('Zoom in')} onClick={() => mapRef.current?.zoomIn()}>+</button>
                  <button type="button" className="pb-zoom__btn" title={t('Zoom out')} onClick={() => mapRef.current?.zoomOut()}>-</button>
                </div>
              </div>
            )}

            {selectedSiteId && (
              <div className="pb-overlay pb-overlay--bl">
                <div className="pb-glass pb-legend">
                  <div className="pb-legend__title">{t('Legend')}</div>
                  <div className="pb-legend__row"><span className="pb-legend__swatch pb-legend__swatch--geo" />{t('Site geofence')}</div>
                  <div className="pb-legend__row"><span className="pb-legend__swatch pb-legend__swatch--post" />{t('Existing post')}</div>
                  <div className="pb-legend__row"><span className="pb-legend__swatch pb-legend__swatch--pending" />{t('New post')}</div>
                </div>
              </div>
            )}

            {selectedSiteId && (
              <div className="pb-overlay pb-overlay--bc">
                <div className="pb-glass pb-actionbar">
                  {clearConfirmPending ? (
                    <div className="pb-actionbar__confirm">
                      <span className="pb-actionbar__confirm-label">{t('Clear draft point?')}</span>
                      <button
                        type="button"
                        className="pb-actionbar__confirm-yes"
                        onClick={clearDraft}
                      >
                        {t('Clear')}
                      </button>
                      <button
                        type="button"
                        className="pb-actionbar__confirm-no"
                        onClick={() => setClearConfirmPending(false)}
                      >
                        {t('Cancel')}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="pb-actionbar__btn"
                      onClick={() => setClearConfirmPending(true)}
                      disabled={!pendingPosition}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M20 20H7l-4-4 9-9 7 7-5 5" /><line x1="14" y1="6" x2="20" y2="12" /></svg>
                      {t('Clear point')}
                    </button>
                  )}
                  <div className="pb-actionbar__div" />
                  <button
                    type="button"
                    className="pb-actionbar__btn"
                    onClick={() => pendingPosition && setDraftInspectorOpen(true)}
                    disabled={!pendingPosition}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 113 3L7 19l-4 1 1-4Z" /></svg>
                    {t('Edit draft')}
                  </button>
                  <div className="pb-actionbar__div" />
                  <button
                    type="button"
                    className="pb-actionbar__btn pb-actionbar__btn--primary"
                    onClick={() => handleSubmit()}
                    disabled={!canSave}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7" /></svg>
                    {isSubmitting ? 'Saving...' : pendingPosition ? 'Save post' : 'Place post'}
                  </button>
                </div>
              </div>
            )}
          </main>

          {inspectorMode === 'draft' && pendingPosition && (
            <aside className="pb-inspector" data-tour="post-builder-draft">
              <div className="pb-inspector__head">
                <div className="pb-inspector__title-row">
                  <div className="pb-inspector__pin">+</div>
                  <input
                    className="pb-inspector__title"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder={t('New post')}
                  />
                  <button type="button" className="pb-inspector__close" onClick={() => setDraftInspectorOpen(false)} title={t('Close')}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                  </button>
                </div>
                <div className="pb-inspector__sub">
                  <span className="pb-tag pb-tag--route">{t('New post')}</span>
                  <span className="pb-tag pb-tag--accent">{radiusNumber} m radius</span>
                  <span className="pb-tag pb-tag--mono">{formatCoordinates(pendingPosition)}</span>
                </div>
              </div>

              <div className="pb-inspector__body">
                {error && <div className="pb-error-banner">{error}</div>}

                <div className="pb-field">
                  <label className="pb-field__label">{t('Post name')}</label>
                  <input
                    className="pb-input"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder={t('Main entrance')}
                  />
                </div>

                <div className="pb-field">
                  <label className="pb-field__label">{t('Description')}</label>
                  <textarea
                    className="pb-textarea"
                    rows={4}
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    placeholder={t('Operational notes for guards')}
                  />
                </div>

                <div className="pb-field">
                  <label className="pb-field__label">{t('Coordinates')}</label>
                  <div className="pb-input pb-input--mono pb-post-readonly">
                    {formatCoordinates(pendingPosition)}
                  </div>
                </div>

                <div className="pb-field">
                  <label className="pb-field__label">Geofence radius - {radiusNumber} m</label>
                  <div className="pb-radius-slider">
                    <div className="pb-radius-slider__row">
                      <input
                        type="range"
                        min="5"
                        max="500"
                        step="5"
                        value={rangeRadius}
                        style={{ '--p': `${radiusProgress}%` } as React.CSSProperties}
                        onChange={(event) => setGeofenceRadius(event.target.value)}
                      />
                      <span className="pb-radius-slider__value">{radiusNumber} m</span>
                    </div>
                  </div>
                </div>

                <div className="pb-toggle-row">
                  <div className="pb-toggle-row__copy">
                    <strong>{t('Fixed guard post')}</strong>
                    <small>{t('This point appears as a schedulable fixed-post duty for the selected site.')}</small>
                  </div>
                </div>

                <button
                  type="button"
                  className="pb-btn pb-btn--primary pb-post-submit"
                  onClick={() => handleSubmit()}
                  disabled={!canSave}
                >
                  {isSubmitting ? 'Saving...' : 'Save post'}
                </button>
              </div>
            </aside>
          )}

          {inspectorMode === 'existing' && selectedExistingPost && (
            <aside className="pb-inspector">
              <div className="pb-inspector__head">
                <div className="pb-inspector__title-row">
                  <div className="pb-inspector__pin">
                    {selectedExistingMapIndex >= 0 ? selectedExistingMapIndex + 1 : 'P'}
                  </div>
                  <div className="pb-inspector__title pb-inspector__title--static">
                    {selectedExistingPost.name}
                  </div>
                  <button type="button" className="pb-inspector__close" onClick={() => setSelectedPostId(null)} title={t('Close')}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                  </button>
                </div>
                <div className="pb-inspector__sub">
                  <span className="pb-tag pb-tag--reuse">{t('Existing')}</span>
                  <span className="pb-tag pb-tag--accent">{formatRadius(selectedExistingPost.geofence_radius_m)}</span>
                  <span className="pb-tag pb-tag--mono">{selectedExistingPosition ? t('Mapped') : t('No map point')}</span>
                </div>
              </div>

              <div className="pb-inspector__body">
                <div className="pb-post-detail-list">
                  <div className="pb-post-detail-row">
                    <span>{t('Site')}</span>
                    <strong>{selectedSite?.name ?? 'Unknown site'}</strong>
                  </div>
                  <div className="pb-post-detail-row">
                    <span>{t('Coordinates')}</span>
                    <strong>{formatCoordinates(selectedExistingPosition)}</strong>
                  </div>
                  <div className="pb-post-detail-row">
                    <span>{t('Geofence')}</span>
                    <strong>{formatRadius(selectedExistingPost.geofence_radius_m)}</strong>
                  </div>
                </div>

                <div className="pb-post-description">
                  <span>{t('Description')}</span>
                  <p>{selectedExistingPost.description || 'No description provided.'}</p>
                </div>

                <button
                  type="button"
                  className="pb-btn pb-btn--danger pb-post-submit"
                  onClick={() => setDeleteConfirmPostId(selectedExistingPost.id)}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" /></svg>
                  {t('Delete post')}
                </button>
              </div>
            </aside>
          )}
        </div>
      </div>

      {toast && (
        <div className={`pb-toast${toast.isError ? ' pb-toast--error' : ''}`}>
          <span className="pb-toast__dot" />
          {toast.msg}
        </div>
      )}

      {deleteConfirmPostId && (
        <ConfirmDeleteModal
          title={t('Delete post')}
          isDeleting={isDeleting}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteConfirmPostId(null)}
        />
      )}

      {exitConfirmPending && onExit && (
        <div className="pb-exit-overlay">
          <div className="pb-exit-modal">
            <div className="pb-exit-modal__icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
            </div>
            <h2 className="pb-exit-modal__title">{t('Leave post builder?')}</h2>
            <p className="pb-exit-modal__body">
              {t('You have an unsaved post in progress. Leaving now will discard its details and map point.')}
            </p>
            <div className="pb-exit-modal__actions">
              <button type="button" className="pb-btn pb-btn--ghost" onClick={() => setExitConfirmPending(false)}>
                {t('Stay and finish')}
              </button>
              <button type="button" className="pb-btn pb-btn--danger" onClick={onExit}>
                {t('Leave anyway')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
